import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Send, Upload, X, Check, Loader2, Sparkles, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import FictionalChart from "@/components/FictionalChart";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

const TRADE_TYPES = [
  { value: "scalp", label: "Scalp", hint: "Minuten" },
  { value: "daytrade", label: "Daytrade", hint: "innerhalb eines Tages" },
  { value: "swingtrade", label: "Swingtrade", hint: "Tage bis Wochen" },
  { value: "hodl", label: "Hodl-Position", hint: "Monate bis Jahre" },
];

const emptyForm = {
  name: "",
  trade_types: [],
  rough_strategy: "",
  sl_description: "",
  tp_description: "",
  additional_notes: "",
};

export default function StrategyDialog({ open, onOpenChange, onCreated, resumeStrategy = null, resumeMode = "reflect" }) {
  const [phase, setPhase] = useState("form"); // form | reflect
  const [form, setForm] = useState(emptyForm);
  const [screenshot, setScreenshot] = useState(""); // Data URL
  const [strategy, setStrategy] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [chatBusy, setChatBusy] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [changeNote, setChangeNote] = useState("");
  const fileRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setErr("");
    setChatInput("");
    setChangeNote("");
    if (resumeStrategy) {
      // Bestehende (Entwurfs- oder aktive) Strategie fortsetzen.
      setStrategy(resumeStrategy);
      setForm({
        name: resumeStrategy.name || "",
        trade_types: resumeStrategy.trade_types || [],
        rough_strategy: resumeStrategy.rough_strategy || "",
        sl_description: resumeStrategy.sl_description || "",
        tp_description: resumeStrategy.tp_description || "",
        additional_notes: resumeStrategy.additional_notes || "",
      });
      setScreenshot(resumeStrategy.chart_screenshot || "");
      if (resumeMode === "form") {
        // Direkt ins Formular springen (Felder bearbeiten). Nach dem Speichern geht's in reflect.
        setPhase("form");
      } else {
        setPhase("reflect");
        // Falls noch keine Coach-Reflexion vorhanden ist, jetzt eine anstoßen
        if (!(resumeStrategy.coach_messages || []).some((m) => m.role === "assistant")) {
          (async () => {
            try {
              const res = await axios.post(
                `${API}/api/strategies/${resumeStrategy.id}/reflect`,
                {},
                { withCredentials: true }
              );
              setStrategy(res.data);
            } catch (e) {
              /* still ok */
            }
          })();
        }
      }
    } else {
      setPhase("form");
      setForm(emptyForm);
      setScreenshot("");
      setStrategy(null);
    }
  }, [open, resumeStrategy, resumeMode]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [strategy?.coach_messages?.length]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleType = (v) =>
    setForm((f) => ({
      ...f,
      trade_types: f.trade_types.includes(v)
        ? f.trade_types.filter((x) => x !== v)
        : [...f.trade_types, v],
    }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErr("Bitte ein Bild hochladen (PNG/JPG).");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setErr("Bild ist zu groß (max. 4 MB).");
      return;
    }
    setErr("");
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result || "");
    reader.readAsDataURL(file);
  };

  const submitForm = async () => {
    setErr("");
    if (!form.name.trim()) {
      setErr("Bitte gib einen Namen für die Strategie an.");
      return;
    }
    if (form.trade_types.length === 0) {
      setErr("Bitte wähle mindestens eine Trade-Art aus.");
      return;
    }
    setBusy(true);
    try {
      let s;
      if (resumeStrategy) {
        // Bestehende Strategie: nur die Felder aktualisieren (löst ggf. auto-Versioning aus).
        const payload = { ...form };
        if (screenshot !== (resumeStrategy.chart_screenshot || "")) {
          payload.chart_screenshot = screenshot;
        }
        if (changeNote.trim()) payload.change_note = changeNote.trim();
        const patched = await axios.patch(
          `${API}/api/strategies/${resumeStrategy.id}`,
          payload,
          { withCredentials: true }
        );
        s = patched.data;
        setChangeNote("");
      } else {
        const created = await axios.post(`${API}/api/strategies`, form, {
          withCredentials: true,
        });
        s = created.data;
        if (screenshot) {
          const patched = await axios.patch(
            `${API}/api/strategies/${s.id}`,
            { chart_screenshot: screenshot },
            { withCredentials: true }
          );
          s = patched.data;
        }
      }
      setStrategy(s);
      // Beim Bearbeiten einer bestehenden Strategie im Form-Modus: einfach speichern
      // und Dialog schließen. Nur bei Neuanlage automatisch Reflexion anstoßen.
      if (!resumeStrategy) {
        const reflected = await axios.post(
          `${API}/api/strategies/${s.id}/reflect`,
          {},
          { withCredentials: true }
        );
        setStrategy(reflected.data);
        setPhase("reflect");
      } else if (resumeMode === "form") {
        toast.success("Änderungen gespeichert", {
          description: "Deine Strategie wurde aktualisiert.",
        });
        onCreated?.(s);
        onOpenChange(false);
        return;
      } else {
        setPhase("reflect");
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const sendChat = async (retryMessage) => {
    const message = (retryMessage ?? chatInput).trim();
    if (!message || !strategy) return;
    if (!retryMessage) setChatInput("");
    setChatBusy(true);
    // Optimistisch Nutzernachricht anzeigen (nur beim ersten Versuch)
    if (!retryMessage) {
      setStrategy((s) => ({
        ...s,
        coach_messages: [
          ...(s.coach_messages || []),
          { role: "user", content: message, at: new Date().toISOString() },
        ],
      }));
    }
    try {
      const res = await axios.post(
        `${API}/api/strategies/${strategy.id}/coach`,
        { message },
        { withCredentials: true, timeout: 90000 }
      );
      setStrategy(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Coach ist gerade nicht erreichbar.";
      // Backend hat die User-Message trotzdem persistiert → beim erneuten Senden nahtlos weiter
      toast.error("Coach-Antwort fehlgeschlagen", {
        description: detail,
        duration: 10000,
        action: {
          label: "Erneut senden",
          onClick: () => sendChat(message),
        },
      });
      // Damit User seine Nachricht nicht neu tippen muss, falls er den Toast verpasst:
      if (!retryMessage) setChatInput(message);
    } finally {
      setChatBusy(false);
    }
  };

  const confirmAndActivate = async () => {
    if (!strategy) return;
    setBusy(true);
    try {
      const payload = { confirmed: true, status: "aktiv" };
      if (changeNote.trim()) payload.change_note = changeNote.trim();
      const res = await axios.patch(
        `${API}/api/strategies/${strategy.id}`,
        payload,
        { withCredentials: true }
      );
      toast.success("Strategie gespeichert", {
        description: `„${res.data.name}" ist jetzt aktiv.`,
      });
      onCreated?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error("Aktivieren fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const saveAsDraft = async () => {
    if (!strategy) return;
    toast.success("Als Entwurf gespeichert", {
      description: `„${strategy.name}" liegt jetzt unter „Entwürfe".`,
    });
    onCreated?.(strategy);
    onOpenChange(false);
  };

  const chatMessages = useMemo(
    () => (strategy?.coach_messages || []).filter((m) => m.role !== "system_context"),
    [strategy]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="strategy-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {resumeStrategy ? `Strategie fortsetzen — ${resumeStrategy.name}` : "Neue Strategie"}
          </DialogTitle>
          <DialogDescription>
            {phase === "form"
              ? "Beschreibe deine Strategie in eigenen Worten. Der KI-Coach hakt danach nach — er gibt keine Empfehlungen, sondern fragt."
              : resumeStrategy
              ? "Setze den Coach-Dialog fort oder aktiviere die Strategie, sobald sie für dich stimmt."
              : "Der Coach hat deine Strategie gelesen. Stimmt sein Verständnis? Sonst frag ihn zurück."}
          </DialogDescription>
        </DialogHeader>

        {phase === "form" && (
          <div
            className="flex-1 overflow-y-auto pr-1 -mr-1 pt-2 space-y-5"
            data-testid="strategy-form"
          >
            <div>
              <Label htmlFor="strategy-name">
                Name der Strategie <span className="text-primary">*</span>
              </Label>
              <Input
                id="strategy-name"
                placeholder="z. B. Momentum-Ausbruch DAX"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                data-testid="strategy-name-input"
                className="mt-1.5"
                autoFocus
              />
            </div>

            <div>
              <Label>
                Art des Trades <span className="text-primary">*</span>
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Mehrfachauswahl möglich — die KI passt ihre Rückfragen an.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                {TRADE_TYPES.map((t) => {
                  const active = form.trade_types.includes(t.value);
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => toggleType(t.value)}
                      data-testid={`strategy-tradetype-${t.value}`}
                      className={`text-left px-3 py-3 rounded-xl border transition-colors ${
                        active
                          ? "bg-primary/20 border-primary/60 text-primary-foreground"
                          : "bg-transparent border-border text-foreground/80 hover:bg-secondary/60"
                      }`}
                    >
                      <div className="font-medium">{t.label}</div>
                      <div className="text-[11px] text-muted-foreground">{t.hint}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="strategy-rough">Grobe Strategie</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Optional. Worum geht es kurz? Welches Marktverhalten nutzt du aus?
              </p>
              <Textarea
                id="strategy-rough"
                rows={3}
                value={form.rough_strategy}
                onChange={(e) => set("rough_strategy", e.target.value)}
                data-testid="strategy-rough-input"
                className="mt-1.5"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="strategy-sl">Wo würdest du den SL setzen?</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Optional. Freitext.
                </p>
                <Textarea
                  id="strategy-sl"
                  rows={3}
                  value={form.sl_description}
                  onChange={(e) => set("sl_description", e.target.value)}
                  data-testid="strategy-sl-input"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="strategy-tp">Wo würdest du TP setzen?</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Optional. Ein oder mehrere Ziele möglich.
                </p>
                <Textarea
                  id="strategy-tp"
                  rows={3}
                  value={form.tp_description}
                  onChange={(e) => set("tp_description", e.target.value)}
                  data-testid="strategy-tp-input"
                  className="mt-1.5"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="strategy-add">
                Ergänzungen zum Tradeverlauf
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Optional. z. B. Nachkauf-Regeln, Zeitfenster, Ausschlusskriterien.
              </p>
              <Textarea
                id="strategy-add"
                rows={3}
                value={form.additional_notes}
                onChange={(e) => set("additional_notes", e.target.value)}
                data-testid="strategy-add-input"
                className="mt-1.5"
              />
            </div>

            <div className="rounded-xl border border-dashed border-border p-4 space-y-3">
              <div className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Label>Chart-Screenshot hochladen (optional)</Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Der Coach kann sich am Screenshot orientieren — hilft bei komplexen Setups. Max. 4 MB.
              </p>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={onFile}
                className="hidden"
                data-testid="strategy-screenshot-file"
              />
              {screenshot ? (
                <div className="flex items-start gap-3">
                  <img
                    src={screenshot}
                    alt="Chart-Screenshot"
                    className="rounded-lg border border-border max-h-40"
                    data-testid="strategy-screenshot-preview"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setScreenshot("");
                      if (fileRef.current) fileRef.current.value = "";
                    }}
                    data-testid="strategy-screenshot-remove"
                    className="rounded-full"
                  >
                    <X className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} /> Entfernen
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  data-testid="strategy-screenshot-upload"
                  className="rounded-full"
                >
                  <Upload className="mr-1.5 h-4 w-4" strokeWidth={1.5} /> Bild auswählen
                </Button>
              )}
            </div>
          </div>
        )}

        {phase === "reflect" && strategy && (
          <div
            className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-5 gap-4 pt-2"
            data-testid="strategy-reflect"
          >
            <div className="lg:col-span-3 overflow-y-auto space-y-3 pr-1">
              <FictionalChart
                seed={strategy.chart_seed}
                tradeTypes={strategy.trade_types}
                hasSL={!!strategy.sl_description}
                hasTP={!!strategy.tp_description}
                hasScreenshot={strategy.has_screenshot}
              />
              {strategy.has_screenshot && strategy.chart_screenshot && (
                <div>
                  <div className="overline mb-1">Dein Screenshot</div>
                  <img
                    src={strategy.chart_screenshot}
                    alt="Hochgeladener Chart"
                    className="rounded-lg border border-border max-h-56"
                  />
                </div>
              )}
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="overline mb-2">Zusammenfassung</div>
                <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-sm">
                  <dt className="text-muted-foreground">Name</dt>
                  <dd>{strategy.name}</dd>
                  <dt className="text-muted-foreground">Trade-Art</dt>
                  <dd className="capitalize">{strategy.trade_types.join(", ")}</dd>
                  <dt className="text-muted-foreground">Strategie</dt>
                  <dd className="whitespace-pre-wrap">{strategy.rough_strategy || "—"}</dd>
                  <dt className="text-muted-foreground">SL</dt>
                  <dd className="whitespace-pre-wrap">{strategy.sl_description || "—"}</dd>
                  <dt className="text-muted-foreground">TP</dt>
                  <dd className="whitespace-pre-wrap">{strategy.tp_description || "—"}</dd>
                  <dt className="text-muted-foreground">Ergänzung</dt>
                  <dd className="whitespace-pre-wrap">{strategy.additional_notes || "—"}</dd>
                </dl>
              </div>
            </div>

            <div className="lg:col-span-2 flex flex-col rounded-xl border border-border bg-card overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
                <Sparkles className="h-4 w-4 text-primary-foreground/80" strokeWidth={1.5} />
                <div className="font-heading text-sm">KI-Coach</div>
              </div>
              <div
                className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
                data-testid="strategy-chat-messages"
              >
                {chatMessages.length === 0 && (
                  <div className="text-sm text-muted-foreground italic">
                    Der Coach denkt gerade nach…
                  </div>
                )}
                {chatMessages.map((m, i) => (
                  <div
                    key={i}
                    className={`text-sm leading-relaxed ${
                      m.role === "assistant" ? "text-foreground" : "text-primary-foreground"
                    }`}
                    data-testid={`strategy-chat-msg-${m.role}-${i}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                      {m.role === "assistant" ? "Coach" : "Du"}
                    </div>
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  </div>
                ))}
                {chatBusy && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} />
                    Coach überlegt…
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              <div className="border-t border-border p-3 flex gap-2">
                <Input
                  placeholder="Frag zurück oder ergänze etwas…"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendChat();
                    }
                  }}
                  data-testid="strategy-chat-input"
                  disabled={chatBusy}
                />
                <Button
                  type="button"
                  onClick={sendChat}
                  disabled={!chatInput.trim() || chatBusy}
                  data-testid="strategy-chat-send"
                  className="rounded-full h-10 px-4"
                >
                  <Send className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          </div>
        )}

        {err && (
          <div
            className="text-sm text-[hsl(0_66%_72%)] mt-2"
            data-testid="strategy-form-error"
          >
            {err}
          </div>
        )}

        <DialogFooter className="pt-4 border-t border-border mt-2 flex-col-reverse sm:flex-row sm:justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="strategy-cancel-btn"
            disabled={busy}
          >
            Abbrechen
          </Button>
          {phase === "form" ? (
            <Button
              type="button"
              onClick={submitForm}
              disabled={busy}
              className="rounded-full h-11 px-5"
              data-testid="strategy-submit-btn"
            >
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                  {resumeStrategy && resumeMode === "form" ? "Wird gespeichert…" : "Coach wird gefragt…"}
                </>
              ) : resumeStrategy && resumeMode === "form" ? (
                <>
                  <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.5} /> Änderungen speichern
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.5} /> An Coach senden
                </>
              )}
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={saveAsDraft}
                disabled={busy}
                data-testid="strategy-draft-btn"
                className="rounded-full h-11 px-5"
              >
                Als Entwurf schließen
              </Button>
              <Button
                type="button"
                onClick={confirmAndActivate}
                disabled={busy}
                data-testid="strategy-confirm-btn"
                className="rounded-full h-11 px-5"
              >
                <Check className="mr-2 h-4 w-4" strokeWidth={1.5} /> Verstanden — Strategie aktivieren
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
